/*
  (c) 2026 Fluxonix Inc.
*/

import React, { useEffect, useState } from "react";
import { api } from "../api";
import RequireRole from "../components/RequireRole";

const WORKFLOW_LABELS = {
  supplier_intake: "Supplier Intake",
};

function formatDate(dateString) {
  return new Date(dateString).toLocaleString();
}

function truncateId(id) {
  if (!id) return "";
  return id.slice(0, 8) + "…";
}

function getRiskBorderClass(risk) {
  if (risk === "high") return "border-l-red-600";
  if (risk === "medium") return "border-l-orange-500";
  if (risk === "low") return "border-l-emerald-600";
  return "border-l-transparent";
}

function getRiskBadgeClass(risk) {
  if (risk === "high") return "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400";
  if (risk === "medium") return "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400";
  if (risk === "low") return "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400";
  return "bg-slate-50 dark:bg-slate-800 text-slate-500";
}

export default function DecisionArtifacts() {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArtifacts = async () => {
      try {
        const res = await api.get("/api/admin/decision-artifacts");
        if (Array.isArray(res.data)) {
          setArtifacts(res.data);
        }
      } catch (err) {
        console.error("Failed to load decision artifacts:", err);
        setError("Failed to load decision artifacts.");
      } finally {
        setLoading(false);
      }
    };
    fetchArtifacts();
  }, []);

  if (loading) {
    return (
      <div className="bg-background-light dark:bg-background-dark font-display min-h-screen flex items-center justify-center">
        <div className="size-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <RequireRole role="admin">
      <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-slate-100 min-h-screen px-4 py-8 sm:py-10">
        <div className="w-full max-w-2xl mx-auto">

          {/* Page header */}
          <div className="flex flex-col items-center mb-8">
            <div className="bg-primary/10 p-3 rounded-lg mb-4">
              <span className="material-symbols-outlined text-primary text-4xl">gavel</span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Governance Timeline
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              Auditable record of automated decisions and human sign-offs
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm mb-6">
              {error}
            </div>
          )}

          {/* Empty state */}
          {artifacts.length === 0 ? (
            <div className="bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
              <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
                <span className="material-symbols-outlined text-primary text-sm mt-0.5">info</span>
                <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                  No decision artifacts recorded yet. They will appear here once the governance workflow produces sign-offs.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {artifacts.map((artifact) => (
                <div
                  key={artifact.id}
                  className={`bg-white dark:bg-slate-900/50 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 border-l-4 ${getRiskBorderClass(artifact.risk_level)}`}
                >
                  <div className="p-6">
                    {/* Workflow label + risk badge */}
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                        {WORKFLOW_LABELS[artifact.workflow_type] || artifact.workflow_type}
                      </span>
                      {artifact.risk_level && (
                        <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-lg ${getRiskBadgeClass(artifact.risk_level)}`}>
                          {artifact.risk_level} risk
                        </span>
                      )}
                    </div>

                    {/* Decision statement */}
                    <p className="text-base font-semibold text-slate-900 dark:text-white leading-snug">
                      {artifact.decision_statement}
                    </p>

                    {/* Signoff line */}
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
                      {artifact.human_signoff_by}
                      <span className="mx-1.5">·</span>
                      {formatDate(artifact.created_at)}
                    </p>

                    {/* Divider */}
                    <div className="my-4 border-t border-slate-100 dark:border-slate-800" />

                    {/* ERP info notice */}
                    <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
                      <span className="material-symbols-outlined text-primary text-sm mt-0.5">database</span>
                      <div>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {artifact.erp_object_type?.replace("_", " ")}
                        </p>
                        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                          Ref: {truncateId(artifact.erp_reference_id)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400 dark:text-slate-500">© 2026 Fluxonix Security. All rights reserved.</p>
          </div>
        </div>

        {/* Background glow */}
        <div className="fixed inset-x-0 bottom-0 -z-10 h-56 sm:h-64 pointer-events-none opacity-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
          <div className="absolute -bottom-28 -left-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
          <div className="absolute -bottom-28 -right-28 w-80 h-80 sm:w-96 sm:h-96 bg-primary rounded-full blur-[120px]"></div>
        </div>
      </div>
    </RequireRole>
  );
}
